// src/routes.js
import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";

// ✅ 홈은 첫 진입 시 즉시 보여야 하므로 동기 import 유지
import HomePage from "./pages/HomePage";

// ✅ 푸터도 즉시 노출되는 영역이므로 즉시 import
import Footer from "./components/Footer";

/**
 * 페이지 코드(청크) 로딩 실패 시 재시도하는 lazy.
 * 앞단 프록시(192.168.0.2)가 요청이 몰리면 503 으로 거절해 페이지가 안 열리는 것을 막는다.
 */
const lazyRetry = (factory, tries = 4) =>
  lazy(async () => {
    for (let i = 0; ; i++) {
      try {
        return await factory();
      } catch (e) {
        if (i >= tries - 1) throw e;
        await new Promise((r) => setTimeout(r, 1000 * 2 ** i));
      }
    }
  });

/* ============================================================
 * 코드 분할(Code Splitting): 페이지별로 별도 chunk 로 분리.
 * 사용자가 해당 경로에 진입할 때만 다운로드됨 → 첫 진입 번들 크기 감소.
 * ============================================================ */

/* ================= Cafe ================= */

const CafeMain   = lazyRetry(() => import("./pages/cafe/CafeMain"));
const CafeGallery = lazyRetry(() => import("./pages/cafe/CafeGallery"));
const CafeMenu    = lazyRetry(() => import("./pages/cafe/CafeMenu"));
const CafeScenes  = lazyRetry(() => import("./pages/cafe/CafeScenes"));

/* ================= Kerzen ================= */
const KerzenziehenMain          = lazyRetry(() => import("./pages/kerzen/KerzenziehenMain"));
const KerzenziehenIntro         = lazyRetry(() => import("./pages/kerzen/KerzenziehenIntro"));
const KerzenziehenHowto         = lazyRetry(() => import("./pages/kerzen/KerzenziehenHowto"));
const KerzenziehenExGallery     = lazyRetry(() => import("./pages/kerzen/KerzenziehenExGallery"));
const KerzenziehenCandleGallery = lazyRetry(() => import("./pages/kerzen/KerzenziehenCandleGallery"));
const KerzenziehenCandleDetail  = lazyRetry(() => import("./pages/kerzen/KerzenziehenCandleDetail"));
const KerzenziehenGallery       = lazyRetry(() => import("./pages/kerzen/KerzenziehenGallery"));
const KerzenziehenScenes        = lazyRetry(() => import("./pages/kerzen/KerzenziehenScenes"));
const BlogPostDetail            = lazyRetry(() => import("./pages/blog/BlogPostDetail"));

/* ================= Preserved ================= */
const PreservedFrameMain             = lazyRetry(() => import("./pages/preserved/PreservedFrameMain"));
const PreservedFrameIntro            = lazyRetry(() => import("./pages/preserved/PreservedFrameIntro"));
const PreservedFrameHowto            = lazyRetry(() => import("./pages/preserved/PreservedFrameHowto"));
const PreservedFrameGallery          = lazyRetry(() => import("./pages/preserved/PreservedFrameGallery"));
const PreservedFrameExGallery        = lazyRetry(() => import("./pages/preserved/PreservedFrameExGallery"));
const PreservedFrameDetail           = lazyRetry(() => import("./pages/preserved/PreservedFrameDetail"));
const PreservedFrameGalleryCombined  = lazyRetry(() => import("./pages/preserved/PreservedFrameGalleryCombined"));
const PreservedFrameScenes           = lazyRetry(() => import("./pages/preserved/PreservedFrameScenes"));

/* ================= Candle Sale ================= */
const ParapinCandle      = lazyRetry(() => import("./pages/candlesale/ParapinCandle"));
const BeeswaxCandle      = lazyRetry(() => import("./pages/candlesale/BeeswaxCandle"));
const CandleDetailPage   = lazyRetry(() => import("./pages/candlesale/CandleDetailPage"));

/* ================= Evening Class ================= */
const EveningClassAbout     = lazyRetry(() => import("./pages/eveningclass/EveningClassAbout"));
const EveningClassOpen      = lazyRetry(() => import("./pages/eveningclass/EveningClassOpen"));
const EveningClassJoin      = lazyRetry(() => import("./pages/eveningclass/EveningClassJoin"));
const EveningClassApply     = lazyRetry(() => import("./pages/eveningclass/EveningClassApply"));
const EveningClassManage    = lazyRetry(() => import("./pages/eveningclass/EveningClassManage"));
const EveningClassAdmin     = lazyRetry(() => import("./pages/eveningclass/EveningClassAdmin"));
const EveningClassApplyForm = lazyRetry(() => import("./pages/eveningclass/EveningClassApplyForm"));

/* ================= Rental ================= */
const RantalspaceMain    = lazyRetry(() => import("./pages/rantalspace/RantalMain"));
const RantalspaceGallery = lazyRetry(() => import("./pages/rantalspace/RantalGallery"));

/* ================= Reservation ================= */
const ReservationRantalspace      = lazyRetry(() => import("./pages/contact/ReservationRantalspace"));
const CandleSelfReservation       = lazyRetry(() => import("./pages/contact/CandleSelfReservation"));
const CandleSelfReservationForm   = lazyRetry(() => import("./pages/contact/CandleSelfReservationForm"));
const FrameSelfReservation        = lazyRetry(() => import("./pages/contact/FrameSelfReservation"));
const FrameSelfReservationForm    = lazyRetry(() => import("./pages/contact/FrameSelfReservationForm"));

/* ================= Mall ================= */
const MallComingSoon     = lazyRetry(() => import("./pages/mall/MallComingSoon"));
const ShoppingMall       = lazyRetry(() => import("./pages/mall/ShoppingMall"));
const OrderPage          = lazyRetry(() => import("./pages/mall/OrderPage"));
const MallCheckout       = lazyRetry(() => import("./pages/mall/MallCheckout"));
const MallPaySuccess     = lazyRetry(() => import("./pages/mall/MallPaySuccess"));
const MallPayFail        = lazyRetry(() => import("./pages/mall/MallPayFail"));
const ProductDetailPage  = lazyRetry(() => import("./pages/mall/ProductDetailPage"));

/* ================= Policy ================= */
const PrivacyPolicy = lazyRetry(() => import("./pages/policy/PrivacyPolicy"));
const RefundPolicy  = lazyRetry(() => import("./pages/policy/RefundPolicy"));

/* ================= Admin ================= */
const PrivateRoute   = lazyRetry(() => import("./pages/admin/PrivateRoute"));
const AdminLogin     = lazyRetry(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazyRetry(() => import("./pages/admin/AdminDashboard"));
const AdminOrders    = lazyRetry(() => import("./pages/admin/AdminOrders"));
const TableOrderStatus = lazyRetry(() => import("./pages/admin/TableOrderStatus"));

/* ================= 디지털 주문판 ================= */
const TableOrderBoard = lazyRetry(() => import("./pages/tableorder/TableOrderBoard"));

/* ================= Display ================= */
const Menuboard   = lazyRetry(() => import("./pages/display/Menuboard"));
const SpecialMenu = lazyRetry(() => import("./pages/display/SpecialMenu"));
// 관리자 '메뉴 관리' 등록 내용이 즉시 반영되는 메뉴보드
const MenuBoardLive = lazyRetry(() => import("./pages/display/MenuBoardLive"));
const SpecialMenuLive = lazyRetry(() => import("./pages/display/SpecialMenuLive"));

/* ================= Game ================= */
const GameLobbyPage      = lazyRetry(() => import("./pages/game/GameLobbyPage"));
const GameRankingPage    = lazyRetry(() => import("./pages/game/GameRankingPage"));
const HallOfFamePage     = lazyRetry(() => import("./pages/game/HallOfFamePage"));
const NumberClickGamePage = lazyRetry(() => import("./pages/game/NumberClickGame"));
const KoreanTypingGamePage = lazyRetry(() => import("./pages/game/KoreanTypingGame"));
const DodgeGamePage      = lazyRetry(() => import("./pages/game/DodgeGame"));
const MathQuizGamePage   = lazyRetry(() => import("./pages/game/MathQuizGame"));

/* ================= Others ================= */
const Visitors = lazyRetry(() => import("./pages/members/Visitors"));

/* ================= Survey ================= */
const SurveyPage = lazyRetry(() => import("./pages/survey/SurveyPage"));

/** ✅ /shop/product/:productId → /mall/product/:productId 로 "진짜 값"을 들고 리다이렉트 */
function ShopProductRedirect() {
  const { productId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!productId) return navigate("/mall", { replace: true });
    navigate(`/mall/product/${encodeURIComponent(String(productId))}`, { replace: true });
  }, [productId, navigate]);

  return null;
}

/** ✅ lazy 페이지 로드 중 표시될 fallback */
function RouteFallback() {
  return (
    <div style={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#999",
      fontSize: 14,
    }}>
      불러오는 중...
    </div>
  );
}

/**
 * AppRoutes
 * today = "YYYY-MM-DD"
 */
export default function AppRoutes({ today }) {
  return (
    <>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* ================= HOME ================= */}
          <Route path="/" element={<HomePage />} />

          {/* ================= CAFE ================= */}
          <Route path="/cafe" element={<CafeMain />} />
          <Route path="/cafe/gallery" element={<CafeGallery />} />
          <Route path="/cafe/menu" element={<CafeMenu />} />
          <Route path="/cafe/scenes" element={<CafeScenes />} />
          <Route path="/cafe/scenes/:id" element={<BlogPostDetail category="cafe" />} />

          {/* ================= KERZEN ================= */}
          <Route path="/kerzenziehen" element={<KerzenziehenIntro />} />
          <Route path="/kerzenziehen/intro" element={<KerzenziehenIntro />} />
          <Route path="/kerzenziehen/gallery" element={<KerzenziehenGallery />} />
          <Route path="/kerzenziehen/scenes" element={<KerzenziehenScenes />} />
          <Route path="/kerzenziehen/scenes/:id" element={<BlogPostDetail category="kerzen" />} />
          <Route path="/kerzenziehen/main" element={<KerzenziehenMain />} />
          <Route path="/kerzenziehen/howto" element={<KerzenziehenHowto />} />
          <Route path="/kerzenziehen/exgallery" element={<KerzenziehenExGallery />} />
          <Route path="/kerzenziehen/candlegallery" element={<KerzenziehenCandleGallery />} />
          <Route path="/kerzenziehen/candle-gallery" element={<KerzenziehenCandleGallery />} />
          <Route path="/kerzenziehen/candle-gallery/:id" element={<KerzenziehenCandleDetail />} />

          {/* ================= PRESERVED ================= */}
          <Route path="/freserved" element={<PreservedFrameIntro />} />
          <Route path="/freserved/intro" element={<PreservedFrameIntro />} />
          <Route path="/freserved/gallery" element={<PreservedFrameGalleryCombined />} />
          <Route path="/freserved/scenes" element={<PreservedFrameScenes />} />
          <Route path="/freserved/scenes/:id" element={<BlogPostDetail category="freserved" />} />
          <Route path="/freserved/main" element={<PreservedFrameMain />} />
          <Route path="/freserved/howto" element={<PreservedFrameHowto />} />
          <Route path="/freserved/exgallery" element={<PreservedFrameExGallery />} />
          <Route path="/freserved/frameexgallery" element={<PreservedFrameExGallery />} />
          <Route path="/freserved/frame-gallery" element={<PreservedFrameGallery />} />
          <Route path="/freserved/framegallery" element={<PreservedFrameGallery />} />
          <Route path="/freserved/frame-gallery/:id" element={<PreservedFrameDetail />} />
          <Route path="/freserved/framegallery/:id" element={<PreservedFrameDetail />} />

          {/* ================= CANDLE SALE ================= */}
          <Route path="/candlesale/parapincandle" element={<ParapinCandle />} />
          <Route path="/candlesale/beeswaxcandle" element={<BeeswaxCandle />} />
          <Route path="/candlesale/detail/:id" element={<CandleDetailPage />} />

          {/* ================= EVENING ================= */}
          <Route path="/eveningclass/about" element={<EveningClassAbout />} />
          <Route path="/eveningclass/join" element={<EveningClassJoin />} />
          <Route path="/eveningclass/open" element={<EveningClassOpen />} />
          <Route path="/eveningclass/apply" element={<EveningClassApply />} />
          <Route path="/eveningclass/manage" element={<EveningClassManage />} />
          <Route path="/eveningclass/admin" element={<EveningClassAdmin />} />
          <Route path="/eveningclass/apply/form" element={<EveningClassApplyForm />} />

          {/* ================= RENTAL ================= */}
          <Route path="/rantalspace" element={<RantalspaceMain />} />
          <Route path="/rantalspace/gallery" element={<RantalspaceGallery />} />

          {/* ================= RESERVATION ================= */}
          <Route path="/reservation/rantalspace" element={<ReservationRantalspace />} />

          {/* Candle */}
          <Route
            path="/reservation/kerzen"
            element={<Navigate to="/reservation/candleselfreservation/" replace />}
          />
          <Route
            path="/reservation/candleselfreservation"
            element={<Navigate to={`/reservation/candleselfreservation/status/${today}`} replace />}
          />
          <Route path="/reservation/candleselfreservation/status/:date" element={<CandleSelfReservation />} />
          <Route path="/reservation/candleselfreservation/form/:date/:time" element={<CandleSelfReservationForm />} />

          {/* Frame */}
          <Route
            path="/reservation/frameselfreservation"
            element={<Navigate to={`/reservation/frameselfreservation/status/${today}`} replace />}
          />
          <Route path="/reservation/frameselfreservation/status/:date" element={<FrameSelfReservation />} />
          <Route path="/reservation/frameselfreservation/form/:date/:time" element={<FrameSelfReservationForm />} />

          {/* ================= MALL ================= */}
          <Route path="/mall" element={<ShoppingMall />} />
          <Route path="/malltest" element={<ShoppingMall />} />
          <Route path="/mall-coming-soon" element={<MallComingSoon />} />
          <Route path="/mall/product/:productId" element={<ProductDetailPage />} />
          <Route path="/mall/order" element={<OrderPage />} />
          <Route path="/mall/checkout" element={<MallCheckout />} />
          <Route path="/mall/success" element={<MallPaySuccess />} />
          <Route path="/mall/fail" element={<MallPayFail />} />

          {/* ✅ /shop 호환 */}
          <Route path="/shop" element={<Navigate to="/mall" replace />} />
          <Route path="/shop/product/:productId" element={<ShopProductRedirect />} />

          {/* ================= policy ================= */}
          <Route path="/policy/privacy" element={<PrivacyPolicy />} />
          <Route path="/policy/refund" element={<RefundPolicy />} />

          {/* ================= ADMIN ================= */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<PrivateRoute> <AdminDashboard /> </PrivateRoute>} />
          <Route path="/admin/home" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/dashboard/*" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/tableorders" element={<PrivateRoute><TableOrderStatus /></PrivateRoute>} />

          {/* ================= 디지털 주문판 ================= */}
          <Route path="/tableorder" element={<PrivateRoute><TableOrderBoard /></PrivateRoute>} />

          {/* ================= DISPLAY ================= */}
          <Route path="/display/menuboard" element={<Menuboard />} />
          <Route path="/display/specialmenu" element={<SpecialMenu />} />

          {/* ================= OTHERS ================= */}
          <Route path="/members/visitors" element={<Visitors />} />

          {/* ================= SURVEY ================= */}
          <Route path="/survey" element={<SurveyPage />} />

          {/* ================= GAME ================= */}
          <Route path="/game" element={<GameLobbyPage />} />
          <Route path="/game/ranking" element={<GameRankingPage />} />
          <Route path="/game/halloffame" element={<HallOfFamePage />} />
          <Route path="/game/number-click" element={<NumberClickGamePage />} />
          <Route path="/game/korean-typing" element={<KoreanTypingGamePage />} />
          <Route path="/game/dodge" element={<DodgeGamePage />} />
          <Route path="/game/math-quiz" element={<MathQuizGamePage />} />

          {/* ================= REDIRECT ================= */}
          <Route path="/menu" element={<MenuBoardLive />} />
          <Route path="/smenu" element={<SpecialMenuLive />} />

          {/* ================= 404 ================= */}
          <Route path="*" element={<div className="pt-20 p-6 text-center">페이지가 없습니다.</div>} />
        </Routes>
      </Suspense>

      <Footer />
    </>
  );
}
